import { getIsVinculacaoCuidadoAtiva } from '@atendimento/detail/soap/plano/cuidado-compartilhado/cuidado-compartilhado-form/utils-cuidadoCompartilhado'
import { HFlow, SelectMenuItem, Text } from 'bold-ui'
import { SelectDownshiftMenuProps } from 'bold-ui/lib/components/Select/SelectSingle/SelectDownshiftMenu'
import { CustomSelectFieldProps, SelectField } from 'components/form/final-form'
import { useFlags } from 'config/useFlagsContext'
import { CboCuidadoCompartilhadoSelectFieldDocument } from 'graphql/hooks.generated'
import {
  CboCuidadoCompartilhadoSelectFieldQuery,
  CboCuidadoCompartilhadoSelectFieldQueryVariables,
  CbosCuidadoCompartilhadoDto,
} from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { partition } from 'util/array'

import { RenderGrupoSelectHeader } from '../RenderGrupoSelectHeader'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type CboCuidadoCompartilhadoSelectModel = CbosCuidadoCompartilhadoDto & { id?: string }

type CboCuidadoCompartilhadoSelectFieldProps = CustomSelectFieldProps<CboCuidadoCompartilhadoSelectModel> & {
  isAtendimentoOdonto?: boolean
}

const ID_GRUPOS_CBOS_VINCULADOS = 'grupoCbosVinculados'
const ID_GRUPOS_CBOS_NAO_VINCULADOS = 'grupoCbosNaoVinculados'

const GRUPO_CBO_VINCULADOS_HEADER_ITEM: CboCuidadoCompartilhadoSelectModel = {
  id: ID_GRUPOS_CBOS_VINCULADOS,
  cbo: null,
  isVinculado: null,
}

const GRUPO_CBO_NAO_VINCULADOS_HEADER_ITEM: CboCuidadoCompartilhadoSelectModel = {
  id: ID_GRUPOS_CBOS_NAO_VINCULADOS,
  cbo: null,
  isVinculado: null,
}

export function CboCuidadoCompartilhadoSelectField(props: CboCuidadoCompartilhadoSelectFieldProps) {
  const flags = useFlags()

  const { isAtendimentoOdonto = false, ...rest } = props
  const isVinculacaoAtiva = getIsVinculacaoCuidadoAtiva(flags, isAtendimentoOdonto)

  const {
    selectProps: { items, ...asyncPropsRest },
  } = useAsyncQuerySelect<
    CboCuidadoCompartilhadoSelectModel,
    CboCuidadoCompartilhadoSelectFieldQuery,
    CboCuidadoCompartilhadoSelectFieldQueryVariables
  >({
    query: CboCuidadoCompartilhadoSelectFieldDocument,
    extractItems: (data) => data?.cbosVinculacoesServico?.content,
    variables: (inputQuery) => ({
      input: {
        query: inputQuery,
        onlyEmulti: !isVinculacaoAtiva && isAtendimentoOdonto,
        onlyOdontologico: isVinculacaoAtiva && isAtendimentoOdonto,
        pageParams: pageParams,
      },
    }),
  })

  const [cbosComVinculacao, cbosSemVinculacao] = partition(items, (item) => item.isVinculado)

  const newItems: ReadonlyArray<CboCuidadoCompartilhadoSelectModel> = useMemo(
    () =>
      isVinculacaoAtiva
        ? [
            GRUPO_CBO_VINCULADOS_HEADER_ITEM,
            ...cbosComVinculacao,
            GRUPO_CBO_NAO_VINCULADOS_HEADER_ITEM,
            ...cbosSemVinculacao,
          ]
        : items,
    [isVinculacaoAtiva, cbosComVinculacao, cbosSemVinculacao, items]
  )

  const hasCboVinculados = cbosComVinculacao.length > 0
  const hasCboNaoVinculados = cbosSemVinculacao.length > 0

  const Item = (
    itemProps: SelectDownshiftMenuProps<CboCuidadoCompartilhadoSelectModel> & {
      item: CboCuidadoCompartilhadoSelectModel
      index: number
    }
  ) => {
    const {
      index,
      downshift: { getItemProps, highlightedIndex },
      item,
      renderItem,
    } = itemProps

    const renderGrupoCbosVinculados = itemProps.item?.id === ID_GRUPOS_CBOS_VINCULADOS
    const renderGrupoCbosNaoVinculados = itemProps.item?.id === ID_GRUPOS_CBOS_NAO_VINCULADOS

    if (renderGrupoCbosVinculados) {
      return (
        <RenderGrupoSelectHeader
          titulo='Vinculadas'
          emptyMessage={!hasCboVinculados && 'Nenhum CBO disponível.'}
          itemProps={getItemProps({ item, disabled: true })}
        />
      )
    }

    if (renderGrupoCbosNaoVinculados) {
      return (
        <RenderGrupoSelectHeader
          titulo='Não vinculadas'
          emptyMessage={!hasCboNaoVinculados && 'Nenhum CBO disponível.'}
          itemProps={getItemProps({ item, disabled: true })}
        />
      )
    }

    return (
      <SelectMenuItem selected={highlightedIndex === index} {...getItemProps({ item })}>
        {renderItem(item)}
      </SelectMenuItem>
    )
  }
  return (
    <SelectField<CboCuidadoCompartilhadoSelectModel>
      itemToString={itemToString}
      renderItem={renderItem}
      items={newItems}
      components={{
        Item,
      }}
      {...asyncPropsRest}
      {...rest}
    />
  )
}

const pageParams = {
  ...DEFAULT_SELECT_PAGE_PARAM,
  sort: ['nome'],
}

const itemToString = (item: CboCuidadoCompartilhadoSelectModel) => item?.cbo?.nome

const renderItem = (item: CboCuidadoCompartilhadoSelectModel) => (
  <Fragment>
    <Text fontWeight='bold'>{item.cbo.nome}</Text>
    <HFlow hSpacing={0.5}>
      <Text fontWeight='bold'>Código:</Text>
      {item.cbo.cbo2002}
    </HFlow>
  </Fragment>
)
